import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, firebaseEnabled } from '../lib/firebase';
import { sendEmailOtp, verifyEmailOtp, EMAIL_RE, CODE_RE } from '../lib/authOtp';
import { useDomainBlocklist } from '../hooks/useDomainBlocklist';
import { normalizeEmail } from '../utils/emailUtils';

const RESEND_COOLDOWN_SECONDS = 60;

type Step = 'email' | 'code' | 'success';

export default function SignInModal({ show, onClose: propOnClose }: { show: boolean; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ kind: 'idle' | 'info' | 'success' | 'error'; message?: string }>({ kind: 'idle' });
  const [step, setStep] = useState<Step>('email');
  const [resendCooldown, setResendCooldown] = useState(0);
  const { check: checkDomainBlocklist, isReady: blocklistReady } = useDomainBlocklist();

  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    };
  }, []);

  const startResendCooldown = () => {
    if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownIntervalRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Shared by onClose and handleBackToEmail — resets the flow to a fresh email-entry state.
  const resetToEmailStep = () => {
    if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    setResendCooldown(0);
    setCode('');
    setStatus({ kind: 'idle' });
    setStep('email');
  };

  const onClose = () => {
    resetToEmailStep();
    propOnClose();
  };

  const validateEmail = (e: string) => EMAIL_RE.test(e);
  const validateCode = (c: string) => CODE_RE.test(c);

  const ensureBlocklistReady = () => {
    if (!blocklistReady) {
      setStatus({ kind: 'info', message: 'Preparing sign-in. Please try again in a moment.' });
      return false;
    }
    return true;
  };

  const enforceBlocklist = (address: string) => {
    const result = checkDomainBlocklist(address);
    if (result.blocked) {
      setStatus({ kind: 'error', message: `Email addresses from ${result.domain} cannot be used to sign in.` });
      return true;
    }
    return false;
  };

  // Map backend HttpsError codes for the OTP callables to user-facing copy.
  // httpsCallable errors always come back prefixed (FunctionsError sets
  // `code` to `functions/${code}`) — no bare-code form is ever produced by
  // the client SDK, so only the prefixed form is checked here.
  const otpErrorMessage = (err: any): string => {
    const code = err?.code || '';
    if (code === 'functions/resource-exhausted') {
      return 'Too many attempts. Please wait a bit and request a new code.';
    }
    if (code === 'functions/failed-precondition') {
      return 'That code has expired or was not found. Please request a new one.';
    }
    if (code === 'functions/invalid-argument') {
      return 'Incorrect code. Please check your email and try again.';
    }
    return err?.message || 'Unable to complete sign-in.';
  };

  // Shared by handleSendCode (first send) and handleResend — only the
  // pre-flight validation and the step transition differ between them.
  const doSend = async (targetEmail: string, { isResend }: { isResend: boolean }) => {
    setSending(true);
    try {
      if (!firebaseEnabled || !auth) {
        setStatus({ kind: 'error', message: 'Sign-in is currently unavailable.' });
        return;
      }
      await sendEmailOtp(targetEmail);
      if (!isResend) {
        setEmail(targetEmail);
        setCode('');
        setStep('code');
      }
      startResendCooldown();
      setStatus({
        kind: 'info',
        message: isResend
          ? 'We sent you a new code — check your inbox (and spam folder).'
          : 'We sent a 6-digit code to your email — check your inbox (and spam folder).',
      });
    } catch (err: any) {
      console.error(isResend ? 'sendEmailOtp (resend) error' : 'sendEmailOtp error', err);
      setStatus({ kind: 'error', message: otpErrorMessage(err) });
    } finally {
      setSending(false);
    }
  };

  const handleSendCode = async (ev?: React.FormEvent) => {
    if (ev) ev.preventDefault();
    setStatus({ kind: 'idle' });
    if (!validateEmail(email)) {
      setStatus({ kind: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    if (!ensureBlocklistReady()) {
      return;
    }
    if (enforceBlocklist(email)) {
      return;
    }
    await doSend(normalizeEmail(email), { isResend: false });
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || sending) return;
    setStatus({ kind: 'idle' });
    if (!ensureBlocklistReady()) {
      return;
    }
    if (enforceBlocklist(email)) {
      return;
    }
    await doSend(email, { isResend: true });
  };

  const handleVerify = async (ev?: React.FormEvent) => {
    if (ev) ev.preventDefault();
    setStatus({ kind: 'idle' });
    if (!validateCode(code)) {
      setStatus({ kind: 'error', message: 'Please enter the 6-digit code from your email.' });
      return;
    }
    if (!ensureBlocklistReady()) {
      return;
    }
    if (enforceBlocklist(email)) {
      return;
    }
    setSending(true);
    try {
      if (!firebaseEnabled || !auth) {
        setStatus({ kind: 'error', message: 'Sign-in is currently unavailable.' });
        return;
      }
      setStatus({ kind: 'info', message: 'Verifying code...' });
      const { token } = await verifyEmailOtp(email, code);
      await signInWithCustomToken(auth, token);
      setStep('success');
      setStatus({ kind: 'success', message: 'Signed in successfully.' });
      setTimeout(() => onClose(), 800);
    } catch (err: any) {
      console.error('verifyEmailOtp error', err);
      setStatus({ kind: 'error', message: otpErrorMessage(err) });
    } finally {
      setSending(false);
    }
  };

  const handleBackToEmail = resetToEmailStep;

  const onSubmit = step === 'code' ? handleVerify : handleSendCode;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header className='bg-theme-extend2 text-theme-extend1' closeButton>
        <Modal.Title>{step === 'code' ? 'Enter your code' : step === 'success' ? 'Signed in' : 'Sign in with email'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSubmit}>
        <Modal.Body className='bg-theme-background'>
          {step === 'email' && (
            <>
              <p>Enter your email and we'll send you a 6-digit code to sign in.</p>
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                <strong>Note:</strong> some domains (such as .edu or .org) may filter or delay our sign-in code email due to their security settings. If you don't see it within a minute, please check your spam folder or try an email address with a different domain (such as .com).
              </p>
              <Form.Group controlId="modalSignInEmail">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </Form.Group>
            </>
          )}

          {step === 'code' && (
            <>
              <p>Enter the 6-digit code we sent to <strong>{email}</strong>.</p>
              <Form.Group controlId="modalSignInCode">
                <Form.Label>6-digit code</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  required
                  autoFocus
                />
              </Form.Group>
              <div className="mt-2 d-flex justify-content-between align-items-center">
                <Button
                  type="button"
                  variant="link"
                  className="p-0"
                  onClick={handleBackToEmail}
                  disabled={sending}
                >
                  Use a different email
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="p-0"
                  onClick={handleResend}
                  disabled={sending || resendCooldown > 0 || !blocklistReady}
                >
                  {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
                </Button>
              </div>
            </>
          )}

          {step === 'success' && (
            <p>You're signed in. This window will close automatically.</p>
          )}

          {status.kind !== 'idle' && (
            <div className="mt-3">
              <Alert variant={status.kind === 'error' ? 'danger' : status.kind === 'success' ? 'success' : 'info'}>
                {status.message}
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className='bg-theme-extend2 text-theme-white'>
          <Button variant='ghost' className='bg-theme-secondary text-theme-white' onClick={onClose}>
            {step === 'success' ? 'Close' : 'Cancel'}
          </Button>
          {step !== 'success' && (
            <Button type="submit" variant="primary" className='bg-theme-primary text-theme-white' disabled={sending || !blocklistReady}>
              {sending ? (<><Spinner size="sm" /> {step === 'code' ? 'Verifying…' : 'Sending…'}</>) : (!blocklistReady ? 'Loading…' : (step === 'code' ? 'Verify code' : 'Send code'))}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
